import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * Practice settings — namespaced JSONB blobs keyed by type.
 *
 * The server returns the full shape `{ practice, clinical, scheduling,
 * billing, notification, security }` from GET /api/settings; this module
 * exposes a single query that every feature can subscribe to, plus
 * narrow selectors for the specific keys each feature uses. That keeps
 * the settings fetch deduplicated in the TanStack Query cache while
 * components only re-render when their particular keys change.
 */

export interface PracticeSettings {
    practiceName?: string;
    npi?: string;
    ein?: string;
    timezone?: string;
    phone?: string;
    email?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    taxonomy?: string;
    /** First visible hour on the calendar grid (0–23). Defaults to 7. */
    calStartHour?: number;
    /** Last visible hour on the calendar grid (1–24, > calStartHour). Defaults to 19. */
    calEndHour?: number;
}

export interface SchedulingSettings {
    blockCertExpired?: boolean;
    evvEnabled?: boolean;
}

export interface BillingSettings {
    authExpiryDays?: number;
    authUrgentDays?: number;
    authLowPct?: number;
    /** 'soft' = warn only, 'hard' = block scheduling against exhausted auth. */
    authBlock?: 'soft' | 'hard';
    /** Office Ally submitter/login id (not a password). */
    oaSubmitterId?: string;
    /** Default to test-mode submissions (OATEST filename keyword). */
    oaTestMode?: boolean;
    /** Office Ally SFTP host; SFTP password is env-only. */
    oaSftpHost?: string;
    /** Office Ally SFTP port. */
    oaSftpPort?: number;
    /** Office Ally SFTP username. */
    oaSftpUser?: string;
    // Private-pay invoice fields — rendered onto every generated docx.
    /** "Remit to" name that prints on invoices. Required before invoice create. */
    remitToName?: string;
    /** Multi-line billing address. Required before invoice create. */
    remitToAddress?: string;
    /** Free-text payment instructions (checks payable to, portal link, etc.). */
    paymentInstructions?: string;
    /** Terms string — e.g. "Net 30", "Due on receipt". */
    invoiceTerms?: string;
    /** Footer note printed beneath the line table. */
    invoiceFooterNote?: string;
    /** Default `due_date` offset from `issued_at`, in days. */
    invoiceDueDays?: number;
    /** Parents see their invoices in the portal unless this is explicitly false. */
    portalInvoicesEnabled?: boolean;
    // Phase 5 of pma-stripe-payments. Card-on-file payments are opt-in
    // — the parent-portal Pay button only mounts when this is `true`
    // AND the practice's Stripe Connect account is live. The portal
    // overview endpoint publishes the combined truth on
    // `features.portalPaymentsEnabled`.
    portalPaymentsEnabled?: boolean;
    /** ≤22-char ASCII prefix Stripe stamps on the cardholder's bank statement.
     *  Validated server-side against a clinical-word blocklist; the platform
     *  prefix on the Connect account is layered on top. */
    paymentsStatementDescriptor?: string;
    // Phase 8 of pma-stripe-payments. Per-practice autopay defaults.
    // The per-invoice opt-in flag lives on `invoices.autopay_enabled`;
    // these two seed the default for new invoices + control how far
    // ahead of due-date the scheduler fires.
    /** When true, new invoices land with autopay_enabled=true so
     *  parents don't have to opt each one in. Default false. */
    autopayDefault?: boolean;
    /** Days before due_date the autopay scheduler fires. 0 = on the
     *  due date itself; max 14. Server validator caps at 14 + refuses
     *  negatives. */
    autopayLeadDays?: number;
}

export type NotificationLeadTime1h = '1h' | '2h' | '3h';
export type NotificationLeadTimeDays = '24h' | '48h' | '72h';
export type NotificationLeadTimeAuth = '1m' | '2m' | '3m';

/** Hour of the day (0–23) the portal-message digest fires; practice-local. */
export type NotificationDigestHour = number;

export interface NotificationSettings {
    /** Shared digest send-hour for the portal-message digest (0–23, practice-local). */
    digestHour?: NotificationDigestHour;
    // Staff notification toggles
    /** Master toggle for the staff side of the daily portal-message digest. */
    staffPortalMessage?: boolean;
    staffSessionReminder?: boolean;
    staffSessionReminderTime?: NotificationLeadTime1h;
    staffMissingNote?: boolean;
    staffMissingNoteTime?: NotificationLeadTimeDays;
    staffScheduleChange?: boolean;
    staffAuthExpiring?: boolean;
    staffAuthExpiringTime?: NotificationLeadTimeAuth;
    staffClaimDenied?: boolean;
    // Parent / guardian notification toggles
    /** Master toggle for the parent side of the daily portal-message digest. */
    parentPortalMessage?: boolean;
    parentSessionReminder?: boolean;
    parentSessionReminderTime?: NotificationLeadTime1h;
    parentScheduleChange?: boolean;
    /** Treatment plan ready for parent signature (fires on send-to-portal). */
    parentSignatureRequest?: boolean;
}

export interface SecuritySettings {
    /**
     * Client-enforced idle timeout, minutes. The only PMA-side security
     * setting post-cutover — password policy, MFA, account lockout, and
     * absolute session lifetime are Cognito's responsibility (ADR 0002).
     */
    sessionTimeout?: number;
}

export interface AllSettings {
    practice: PracticeSettings;
    clinical: Record<string, unknown>;
    scheduling: SchedulingSettings;
    billing: BillingSettings;
    notification: NotificationSettings;
    security: SecuritySettings;
    integrations?: Record<string, unknown>;
}

async function fetchSettings(): Promise<AllSettings> {
    const res = await api.get<AllSettings>('/settings');
    return res.data;
}

export function useSettings() {
    return useQuery({
        queryKey: ['settings'] as const,
        queryFn: fetchSettings,
        staleTime: 5 * 60_000,
    });
}

/** Calendar display hours with sane fallbacks when settings haven't loaded. */
export interface CalendarHours {
    start: number;
    end: number;
}

export const DEFAULT_CALENDAR_HOURS: CalendarHours = { start: 7, end: 19 };

export function useCalendarHours(): CalendarHours {
    const { data } = useSettings();
    const start = data?.practice?.calStartHour;
    const end = data?.practice?.calEndHour;
    return {
        start: typeof start === 'number' ? start : DEFAULT_CALENDAR_HOURS.start,
        end: typeof end === 'number' ? end : DEFAULT_CALENDAR_HOURS.end,
    };
}

/**
 * Client-enforced idle-timeout in minutes. Falls back to 15 when settings
 * haven't loaded or the value is out-of-range. The server's absolute JWT
 * lifetime (`sessionMaxMinutes`) is a separate concern enforced at token
 * verification — this hook only drives the UI auto-logout.
 */
export const DEFAULT_IDLE_TIMEOUT_MIN = 15;
export const IDLE_TIMEOUT_MIN_FLOOR = 1;
export const IDLE_TIMEOUT_MIN_CEILING = 120;

export function useIdleTimeoutMinutes(): number {
    const { data } = useSettings();
    const raw = data?.security?.sessionTimeout;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return DEFAULT_IDLE_TIMEOUT_MIN;
    if (raw < IDLE_TIMEOUT_MIN_FLOOR) return IDLE_TIMEOUT_MIN_FLOOR;
    if (raw > IDLE_TIMEOUT_MIN_CEILING) return IDLE_TIMEOUT_MIN_CEILING;
    return raw;
}

/**
 * Office Ally is considered "configured" when submitter id + SFTP host + SFTP
 * user are all set. Password is server-side only (practice_secrets), so we
 * can't check it from the client — the server also enforces this guard on
 * payer create/update.
 */
export function useIsOaConfigured(): boolean {
    const { data } = useSettings();
    const b = data?.billing;
    if (!b) return false;
    return Boolean(
        (b.oaSubmitterId || '').trim()
        && (b.oaSftpHost || '').trim()
        && (b.oaSftpUser || '').trim(),
    );
}

export type SettingsType = 'practice' | 'clinical' | 'scheduling' | 'billing' | 'notification' | 'security' | 'integrations';

export function useUpdateSettings() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ type, updates }: { type: SettingsType; updates: Record<string, unknown> }) => {
            const res = await api.patch<Record<string, unknown>>(`/settings/${type}`, updates);
            return { type, data: res.data };
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
    });
}

/** Downloads a sample invoice .docx rendered from the practice's current
 *  billing settings + logo. No real PHI — the server fills in placeholder
 *  client + line data. Useful for checking layout after a settings edit. */
export function useDownloadInvoicePreview() {
    return useMutation({
        mutationFn: async (): Promise<{ filename: string }> => {
            const res = await api.get('/settings/billing/invoice-preview', {
                responseType: 'blob',
            });

            const cd = res.headers['content-disposition'] as string | undefined;
            const match = cd && /filename="([^"]+)"/.exec(cd);
            const filename = match ? match[1] : 'invoice-preview.docx';

            const objectUrl = URL.createObjectURL(res.data as Blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);

            return { filename };
        },
    });
}
